// src/pages/offlineSales/terminal/components/CartPanel.jsx
import React from 'react';
import { Plus, Minus, Trash2, Percent, Gift, Hand, CreditCard, Banknote, ShoppingCart, ReceiptText, BadgePercent } from 'lucide-react';
import { useLocalization } from '../../../../contexts/LocalizationContext';
import { getImageUrl } from '../../../../utils/imageUtils';
import styles from '../OfflineSalesTerminal.module.css';

const NO_IMAGE_SRC = '/assets/other/no-image-preview.png';

// Swap broken product images for the placeholder (guarded to avoid an error loop)
const handleNoImage = (e) => {
  if (!e.currentTarget.src.endsWith(NO_IMAGE_SRC)) {
    e.currentTarget.src = NO_IMAGE_SRC;
  }
};

const CartPanel = ({
  topSection,
  cartItems,
  orderDiscount,
  totals,
  onUpdateItem,
  onRemoveItem,
  onOpenDiscount,
  onOpenLoyalty,
  onHold,
  onClear,
  onCharge,
  onCash,
  onQuickSale,
  submitting,
  settings,
  customer,
  canChangeUnitPrice,
}) => {
  const { t, formatCurrency, formatNumber } = useLocalization();
  const formatAmount = (amount) => formatNumber(amount, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const { subtotal, itemDiscounts, loyaltyDiscount, grandTotal } = totals;
  const totalDiscount = itemDiscounts + (parseFloat(orderDiscount) || 0) + loyaltyDiscount;

  return (
    <div className={styles.cartPanel}>
      {topSection}

      <div className={styles.cartItems}>
        {cartItems.length === 0 ? (
          <div className={styles.emptyCart}>
            <p>{t('pos.emptyCart')}</p>
            <small>{t('pos.emptyCartHint')}</small>
          </div>
        ) : (
          <>
            <div className={`${styles.cartItem} ${styles.cartItemHeader}`}>
              <span className={styles.cartItemIndex}>#</span>
              <span className={styles.cartHeaderProduct}>Item Name</span>
              <span className={styles.cartHeaderCenter}>Quantity</span>
              <span className={styles.cartHeaderRight}>Price</span>
              <span className={styles.cartHeaderCenter}>Discount</span>
              <span className={styles.cartHeaderRight}>Subtotal</span>
              <span />
            </div>
            {cartItems.map((item, index) => (
              <div key={item.key} className={styles.cartItem}>
                <span className={styles.cartItemIndex}>{index + 1}</span>
                <div className={styles.cartItemThumb}>
                  <img
                    src={item.image ? getImageUrl(item.image) : NO_IMAGE_SRC}
                    alt={item.name}
                    onError={handleNoImage}
                  />
                </div>
                <div className={styles.cartItemInfo}>
                  <div className={styles.cartItemName}>{item.baseName || item.name}</div>
                  {(item.variationLabel || item.sku) && (
                    <div className={styles.cartItemMeta}>
                      {[item.variationLabel, item.sku && `SKU: ${item.sku}`].filter(Boolean).join(' - ')}
                    </div>
                  )}
                </div>
                <div className={styles.cartItemControls}>
                  <button
                    className={styles.qtyBtn}
                    onClick={() => onUpdateItem(item.key, 'quantity', Math.max(0.01, item.quantity - 1))}
                  >
                    <Minus size={14} />
                  </button>
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={item.quantity}
                    onChange={e => onUpdateItem(item.key, 'quantity', e.target.value)}
                    className={styles.qtyInput}
                  />
                  <button
                    className={styles.qtyBtn}
                    onClick={() => onUpdateItem(item.key, 'quantity', item.quantity + 1)}
                  >
                    <Plus size={14} />
                  </button>
                </div>
                <div className={styles.cartItemUnitPrice}>
                  {canChangeUnitPrice ? (
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={item.unitPrice}
                      onChange={e => onUpdateItem(item.key, 'unitPrice', e.target.value)}
                      className={styles.unitPriceInput}
                      title={t('pos.unitPrice')}
                    />
                  ) : (
                    formatAmount(item.unitPrice)
                  )}
                </div>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={item.lineDiscount}
                  onChange={e => onUpdateItem(item.key, 'lineDiscount', e.target.value)}
                  className={styles.discountInput}
                  title={t('pos.lineDiscount')}
                />
                <span className={styles.lineTotal}>
                  {formatAmount(item.quantity * item.unitPrice - (item.lineDiscount || 0))}
                </span>
                <button className={styles.removeBtn} onClick={() => onRemoveItem(item.key)} title={t('pos.removeItem')}>
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </>
        )}
      </div>

      <div className={styles.cartToolbar}>
        {settings?.loyaltyEnabled && customer && !customer.isCashSales && (
          <button className={styles.cartHeaderBtn} onClick={onOpenLoyalty} title={t('pos.redeemLoyalty')}>
            <Gift size={14} />
            {t('pos.redeemLoyalty')}
          </button>
        )}
        <button className={styles.cartHeaderBtn} onClick={onOpenDiscount} title={t('pos.applyDiscount')}>
          <Percent size={14} />
          {t('pos.applyDiscount')} <kbd className={styles.kbdShortcut}>F4</kbd>
        </button>
        <button
          className={`${styles.cartHeaderBtn} ${styles.cartHeaderBtnDanger}`}
          onClick={onClear}
          disabled={cartItems.length === 0}
          title={t('pos.clearCart')}
        >
          <Trash2 size={14} />
          {t('pos.clearCart')} <kbd className={styles.kbdShortcut}>F3</kbd>
        </button>
      </div>

      <div className={styles.summaryStats}>
        <div className={styles.summaryStat}>
          <span className={styles.summaryStatIcon}><ShoppingCart size={16} /></span>
          <span className={styles.summaryStatLabel}>{t('pos.items')}</span>
          <span className={styles.summaryStatValue}>{cartItems.length}</span>
        </div>
        <div className={styles.summaryStat}>
          <span className={styles.summaryStatIcon}><ReceiptText size={16} /></span>
          <span className={styles.summaryStatLabel}>{t('pos.subtotal')}</span>
          <span className={styles.summaryStatValue}>{formatCurrency(subtotal)}</span>
        </div>
        <div
          className={styles.summaryStat}
          title={[
            `${t('pos.itemDiscount')}: ${formatCurrency(itemDiscounts)}`,
            `${t('pos.orderDiscount')}: ${formatCurrency(parseFloat(orderDiscount) || 0)}`,
            `${t('pos.loyaltyDiscount')}: ${formatCurrency(loyaltyDiscount)}`,
          ].join('\n')}
        >
          <span className={styles.summaryStatIcon}><BadgePercent size={16} /></span>
          <span className={styles.summaryStatLabel}>{t('pos.lineDiscount')}</span>
          <span className={styles.summaryStatValue}>{formatCurrency(totalDiscount)}</span>
        </div>
      </div>

      <div className={styles.grandTotalBar}>
        <span>{t('pos.grandTotal')}</span>
        <span className={styles.grandTotalAmount}>{formatCurrency(grandTotal)}</span>
      </div>

      <div className={styles.payActions}>
        <button className={`${styles.payBtn} ${styles.payBtnHold}`} onClick={onHold} disabled={cartItems.length === 0}>
          <Hand size={16} /> Hold
          <kbd className={styles.kbdShortcutInverse}>F2</kbd>
        </button>
        <button className={`${styles.payBtn} ${styles.payBtnMultiple}`} onClick={onCash} disabled={cartItems.length === 0}>
          <CreditCard size={16} /> Multiple
        </button>
        <button className={`${styles.payBtn} ${styles.payBtnCash}`} onClick={onCash} disabled={cartItems.length === 0}>
          <Banknote size={16} /> Cash
          <kbd className={styles.kbdShortcutInverse}>F1</kbd>
        </button>
        <button className={`${styles.payBtn} ${styles.payBtnPayAll}`} onClick={onQuickSale} disabled={cartItems.length === 0 || submitting}>
          <Banknote size={16} /> Pay All
        </button>
      </div>
    </div>
  );
};

export default CartPanel;
